<template>
  <div>
    <div class="px-page mb-8">
      <div class="flex items-center justify-between">
        <h2 class="text-xl font-semibold leading-tight"> Custom Apparel </h2>
      </div>
    </div>
    <div class="card px-page">
      <div class="flex items-center justify-between border-b border-gray-200">
        <nav class="-mb-px flex flex-1 space-x-6 xl:space-x-8" aria-label="Tabs">
          <span v-for="tab in tabs" :key="tab.name" :class="[tab.current ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:border-primary hover:text-primary', 'cursor-pointer whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium']" @click="selectStatus(tab.slug)">
            {{ tab.name }}
          </span>
        </nav>
        <div class="">
          <Listbox v-model="selectedFilter" as="div" class="flex items-center gap-3" @update:model-value="(value) => selectFilter(value)">
            <ListboxLabel class="block text-sm text-gray-700">Apparel Filter</ListboxLabel>
            <div class="relative w-36">
              <ListboxButton class="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6">
                <span class="block truncate">{{ selectedFilter.label }}</span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>

              <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <ListboxOption v-for="filter in filters" :key="filter.value" v-slot="{ active, selected }" as="template" :value="filter">
                    <li :class="[active ? 'bg-primary-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                      <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ filter.label }}</span>

                      <span v-if="selected" :class="[active ? 'text-white' : 'text-primary-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                        <CheckIcon class="h-5 w-5" aria-hidden="true" />
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
        </div>
      </div>
      <section class="mt-8">
        <h2 id="gallery-heading" class="sr-only">Pending Orders</h2>
        <div v-for="custom in orders" :key="custom.id" :class="{ 'ring-primary-200': !custom.blemish, 'ring-red-500': custom.blemish }" class="aspect-h-10 mb-6 block w-full rounded-lg p-4 ring-2">
          <div class="mb-4 flex justify-between">
            <div>
              <div>
                <Link :href="$route('orders.show', custom.order_number)" class="font-medium text-gray-700">Order #{{ custom.order_number }}</Link>
                <p class="mt-1 text-sm font-medium text-gray-700">
                  {{ custom.customer_name }}
                  <span class="ml-2 text-gray-500">{{ custom.ordered_at_formatted }}</span>
                </p>
                <p class="mt-3 text-lg font-medium text-gray-900">{{ custom.product_name }}</p>
              </div>
            </div>
            <div class="space-y-2">
              <button v-if="custom.status === 'pending' || custom.status === 'approved'" type="button" class="btn btn-danger btn-xs btn-block" @click="rejectOrder(custom)">Hold</button>
              <button v-if="custom.status === 'pending' || custom.status === 'rejected'" type="button" class="btn btn-success btn-xs btn-block" @click="printOrder(custom)">Print</button>
              <button v-if="custom.status === 'rejected'" type="button" class="btn btn-danger btn-xs btn-block" @click="cancelOrder(custom)">Cancel</button>
            </div>
          </div>
          <ul role="list" class="z-10 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-x-8">
            <li v-for="data in custom.data" :key="data.title" class="aspect-h-10 block w-full rounded-lg p-4 ring-2 ring-primary-200">
              <div class="space-y-6">
                <p class="text-center font-medium text-gray-800">{{ data.title }}</p>
                <img :src="data.filename" :alt="data.title" class="hover:opacity-75" />
              </div>
            </li>
          </ul>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";
import { Link } from "@inertiajs/vue3";

import { deleteData, fetchData, putData } from "../../composables/CustomOrders";

export default {
  name: "CustomApparel",
  components: {
    CheckIcon,
    ChevronUpDownIcon,
    ListboxOption,
    ListboxOptions,
    ListboxButton,
    ListboxLabel,
    Listbox,
    Link,
  },
  data() {
    return {
      tabs: [
        { name: "Pending Orders", slug: "pending", current: true },
        { name: "On Hold", slug: "rejected", current: false },
      ],
      filters: [
        { label: "All", value: "all" },
        { label: "Hoodie", value: "hoodie" },
        { label: "T-Shirt", value: "t-shirt" },
      ],
      selectedFilter: { label: "All", value: "all" },
      selectedStatus: "pending",
      orders: [],
      imageField: false,
      replacementImage: null,
      loading: false,
      error: null,
    };
  },
  mounted() {
    this.loading = true;
    this.selectStatus("pending");
  },
  methods: {
    selectStatus(status) {
      this.selectedStatus = status;
      this.loading = true;
      fetchData("apparel", status, this.selectedFilter.value)
        .then((data) => {
          this.orders = data;
          this.tabs.forEach((tab) => {
            tab.current = false;
          });
          this.tabs.map((tab) => {
            if (tab.slug === status) {
              tab.current = true;
            }
          });
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    selectFilter(filter) {
      this.loading = true;
      fetchData("apparel", this.selectedStatus, filter.value)
        .then((data) => (this.orders = data))
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    approveOrder(order) {
      this.loading = true;
      putData(order, "approved")
        .then(() => {
          this.orders = this.orders.filter(({ id }) => id !== order.id);
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    rejectOrder(order) {
      this.loading = true;
      putData(order, "rejected")
        .then(() => {
          this.orders = this.orders.filter(({ id }) => id !== order.id);
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancelOrder(order) {
      this.loading = true;
      deleteData(order)
        .then(() => {
          this.orders = this.orders.filter(({ id }) => id !== order.id);
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    printOrder(order) {
      this.loading = true;
      putData(order, "printing")
        .then(() => {
          this.orders = this.orders.filter(({ id }) => id !== order.id);
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
